import BaseCrudService from './BaseCrudService';
import api from './api';

/**
 * Message Service
 * Extends BaseCrudService for messaging and conversation management
 */
class MessageService extends BaseCrudService {
  constructor() {
    super('/api/messages');
  }

  // Get all conversations for current user
  getConversations(page = 1, limit = 20, options = {}) {
    const params = { page, limit };
    if (options && options.teamId) params.teamId = options.teamId;
    return this.customRequest('get', 'conversations', null, { params });
  }

  // Get or create a conversation
  getOrCreateConversation(participantIds, isGroup = false, groupName = null, options = {}) {
    const payload = { participantIds, isGroup, groupName };
    if (options && options.teamId) payload.teamId = options.teamId;
    return this.customRequest('post', 'conversations', payload);
  }

  // Start or fetch a team conversation
  getOrCreateTeamConversation(teamId) {
    return this.customRequest('post', 'conversations', { teamId, isGroup: true });
  }

  // Get single conversation details
  getConversation(conversationId) {
    return this.customRequest('get', `conversations/${conversationId}`);
  }

  // Get messages for a conversation
  getMessages(conversationId, page = 1, limit = 50) {
    return this.customRequest('get', `conversations/${conversationId}/messages`, null, {
      params: { page, limit }
    });
  }

  // Send a message
  sendMessage(conversationId, content, messageType = 'text', replyTo = null, attachment = null) {
    const payload = { conversationId, content, messageType, replyTo };
    if (attachment) payload.attachment = attachment;
    return this.customRequest('post', 'messages', payload);
  }

  // Upload an attachment (image/file)
  async uploadAttachment(file) {
    const formData = new FormData();
    formData.append('file', file);
    return this.request(
      () => api.post(`${this.baseUrl}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      }),
      'Failed to upload attachment'
    );
  }

  // Mark messages as read
  markAsRead(conversationId) {
    return this.customRequest('post', `conversations/${conversationId}/read`);
  }

  // Delete a message
  deleteMessage(messageId) {
    return this.customRequest('delete', `messages/${messageId}`);
  }

  // Edit a message
  editMessage(messageId, content) {
    return this.customRequest('patch', `messages/${messageId}`, { content });
  }

  // Delete (archive) a conversation
  deleteConversation(conversationId) {
    return this.customRequest('delete', `conversations/${conversationId}`);
  }

  // Update conversation (rename group, change avatar, etc.)
  updateConversation(conversationId, updates) {
    return this.customRequest('patch', `conversations/${conversationId}`, updates);
  }

  // Add reaction to message
  addReaction(messageId, emoji) {
    return this.customRequest('post', `messages/${messageId}/reactions`, { emoji });
  }

  // Set typing status
  async setTypingStatus(conversationId, isTyping) {
    try {
      return await this.customRequest('post', `conversations/${conversationId}/typing`, { isTyping });
    } catch (error) {
      console.error('Error setting typing status:', error);
      // Don't throw for typing status errors
      return null;
    }
  }

  // Search users for new conversations
  searchUsers(query) {
    return this.customRequest('get', 'users/search', null, { params: { query } });
  }

  // Search teams for new conversations
  async searchTeams(query) {
    try {
      // Prefer messages API if available
      return await this.customRequest('get', 'teams/search', null, { params: { query } });
    } catch (innerErr) {
      // Fallback path: common teams search endpoint
      return this.request(
        () => api.get('/api/teams/search', { params: { query } }),
        'Failed to search teams'
      );
    }
  }

  // Format conversation display name
  getConversationDisplayName(conversation, currentUserId) {
    if (conversation.isGroup) {
      return conversation.groupName || 'Unnamed Group';
    }

    // For 1-on-1, show the other participant's name
    const otherParticipant = conversation.participants.find(
      p => p._id !== currentUserId
    );
    return otherParticipant ? (otherParticipant.name || 'Unknown User') : 'Unknown User';
  }

  // Format conversation avatar
  getConversationAvatar(conversation, currentUserId) {
    if (conversation.isGroup) {
      return conversation.groupAvatar || null;
    }

    // For 1-on-1, show the other participant's avatar
    const otherParticipant = conversation.participants.find(
      p => p._id !== currentUserId
    );
    return otherParticipant ? otherParticipant.avatar : null;
  }

  // Format time ago
  formatTimeAgo(date) {
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);

    let interval = Math.floor(seconds / 31536000);
    if (interval > 1) return `${interval} years ago`;
    if (interval === 1) return '1 year ago';

    interval = Math.floor(seconds / 2592000);
    if (interval > 1) return `${interval} months ago`;
    if (interval === 1) return '1 month ago';

    interval = Math.floor(seconds / 86400);
    if (interval > 1) return `${interval} days ago`;
    if (interval === 1) return '1 day ago';

    interval = Math.floor(seconds / 3600);
    if (interval > 1) return `${interval} hours ago`;
    if (interval === 1) return '1 hour ago';

    interval = Math.floor(seconds / 60);
    if (interval > 1) return `${interval} minutes ago`;
    if (interval === 1) return '1 minute ago';

    return 'Just now';
  }

  // Format message time
  formatMessageTime(date) {
    const messageDate = new Date(date);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const timeStr = messageDate.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });

    if (messageDate.toDateString() === today.toDateString()) {
      // Today: keep only the time
      return timeStr;
    } else if (messageDate.toDateString() === yesterday.toDateString()) {
      // Yesterday: include time
      return `Yesterday ${timeStr}`;
    } else {
      // Other dates: include date plus time, include year only if not current year
      const dateStr = messageDate.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: messageDate.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
      });
      return `${dateStr} ${timeStr}`;
    }
  }
}

export default new MessageService();
